import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute — requires authentication.
 * If allowedRedirects is provided, also checks that the user's
 * backendRedirectTo matches one of the allowed values.
 * Otherwise redirects to the user's correct page.
 */
export default function ProtectedRoute({ children, allowedRedirects }) {
  const { user, authLoading, backendRedirectTo, getRouteForRedirect } = useAuth();

  if (authLoading) {
    return (
      <div className="app-container">
        <div className="loading-screen">
          <div className="logo-container small">
            <img src="/Vagmee_Logo.png" alt="Vagmee Logo" className="loading-logo" />
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!backendRedirectTo) {
    return (
      <div className="app-container">
        <div className="loading-screen">
          <div className="logo-container small">
            <img src="/Vagmee_Logo.png" alt="Vagmee Logo" className="loading-logo" />
          </div>
          <p>Finalizing authentication...</p>
        </div>
      </div>
    );
  }

  // Role check: if this route requires specific redirect values, enforce them
  if (allowedRedirects && !allowedRedirects.includes(backendRedirectTo)) {
    return <Navigate to={getRouteForRedirect(backendRedirectTo)} replace />;
  }

  return children;
}

/**
 * PublicOnlyRoute — redirects authenticated users to their dashboard.
 * Used for landing page and login screen.
 */
export function PublicOnlyRoute({ children }) {
  const { user, authLoading, backendRedirectTo, getRouteForRedirect } = useAuth();
  const location = useLocation();

  if (authLoading) {
    return (
      <div className="app-container">
        <div className="loading-screen">
          <div className="logo-container small">
            <img src="/Vagmee_Logo.png" alt="Vagmee Logo" className="loading-logo" />
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (user && backendRedirectTo) {
    const searchParams = new URLSearchParams(location.search);
    const returnTo = location.state?.returnTo || searchParams.get('returnTo');
    if (
      typeof returnTo === 'string' &&
      (returnTo.startsWith('/interview/') || returnTo.startsWith('/inst/') || returnTo === '/setup')
    ) {
      return <Navigate to={returnTo} replace />;
    }
    return <Navigate to={getRouteForRedirect(backendRedirectTo)} replace />;
  }

  return children;
}
